<template>
  <div :class="[componentName, 'tce-slider', { 'tce-slider--white': isTextColorWhite }]">
    <h2 v-if="title" class="tce-slider__title">{{ title }}</h2>
    <div ref="sliderWrapper" class="tce-slider__wrapper">
      <slot name="list" />
      <div v-if="showControls" class="tce-slider__controls">
        <button
          class="tce-slider__prev"
          :disabled="isPrevDisabled"
          :aria-label="previousLabel"
          @click="prevSlide"
        ></button>
        <button
          class="tce-slider__next"
          :disabled="isNextDisabled"
          :aria-label="nextLabel"
          @click="nextSlide"
        ></button>
      </div>
      <ul v-if="showControls" class="tce-slider__dots">
        <li v-for="item in dotsAmount" :key="item - 1" class="tce-slider__dot-wrapper">
          <button
            :class="['tce-slider__dot', { active: item - 1 === selectedDotId }]"
            :aria-current="item - 1 === selectedDotId"
            :aria-label="slideOf(item, dotsAmount)"
            @click="selectedSlide(item - 1)"
          ></button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'tce-slider',
};
</script>

<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { debounce } from '../../utilities/timing';
import { AnalyticsHandler } from '../../directives/analyticsHandler';

const SLIDES_LIST_SELECTOR = '.tce-slider__slides';
const SLIDE_SELECTOR = '.tce-slider__slide';
const SLIDE_LABEL_SELECTOR = '.tce-slider__slide-label';
const VISIBLE = 'visible';
const HIDDEN = 'hidden';

const sliderWrapper = ref(null);
const currentIndex = ref(0);
const selectedDotId = ref(0);
const visibleItems = ref();
const dotsAmount = ref();
let slidesAmount;
let slidesList;

const props = defineProps({
  componentName: { type: String, default: '' },
  title: { type: String, default: '' },
  isTextColorWhite: Boolean,
  previousLabel: { type: String, default: '' },
  nextLabel: { type: String, default: '' },
  mobileScreenEnd: { type: Number, default: 1023 },
  mobileCardPerView: { type: Number, default: 1 },
  desktopCardPerView: { type: Number, default: 4 },
  paginationLabelStart: { type: String, default: '' },
  paginationLabelMiddle: { type: String, default: '' },
  analytics: {
    type: {
      type: String,
      event_name: String,
      category: String,
      action: String,
    },
    default: () => {},
  },
});

onMounted(() => {
  slidesList = sliderWrapper?.value.querySelector(SLIDES_LIST_SELECTOR);
  slidesAmount = slidesList.childElementCount;

  slidesLazyLoading();
  visibleItems.value = setVisibleItemsAmount.value;

  window.addEventListener('resize', debounce(onResize, 250));
});

const setVisibleItemsAmount = computed(() => {
  return isMobile.value ? props.mobileCardPerView : props.desktopCardPerView;
});

const isMobile = computed(() => {
  return window.innerWidth < props.mobileScreenEnd;
});

const isPrevDisabled = computed(() => {
  return currentIndex.value === 0;
});

const isNextDisabled = computed(() => {
  return currentIndex.value + visibleItems.value >= slidesAmount;
});

const showControls = computed(() => {
  return slidesAmount > visibleItems.value;
});

watch(
  () => visibleItems.value,
  () => {
    dotsAmount.value = Math.ceil(slidesAmount / visibleItems.value);
  },
);

function scrollSlideIntoView(nextItemId) {
  if (isMobile.value) {
    slidesList.scrollTo({
      left: slidesList.querySelectorAll(SLIDE_SELECTOR)[nextItemId].offsetLeft,
      behavior: 'smooth',
    });
  } else {
    slidesList.style = `transform: translateX(-${nextItemId * (100 / visibleItems.value)}%)`;
  }
}

function nextSlide() {
  const nextItemId = currentIndex.value + visibleItems.value;

  setCurrentSlideIndex(nextItemId, selectedDotId.value + 1);
  scrollSlideIntoView(currentIndex.value);

  if (props.analytics) pushAnalytics(currentIndex.value);
}

function prevSlide() {
  const visibleSlideId = currentIndex.value - visibleItems.value;
  currentIndex.value = visibleSlideId < 0 ? 0 : visibleSlideId;

  setCurrentSlideIndex(currentIndex.value, selectedDotId.value - 1);
  scrollSlideIntoView(currentIndex.value);

  if (props.analytics) pushAnalytics(currentIndex.value);
}

function selectedSlide(id) {
  const nextItemId = visibleItems.value * id;

  setCurrentSlideIndex(nextItemId, id);
  scrollSlideIntoView(currentIndex.value);

  if (props.analytics) pushAnalytics(currentIndex.value);
}

function setCurrentSlideIndex(nextItemId, dotId) {
  const amountOfVisibleSlides = slidesAmount - nextItemId;

  if (amountOfVisibleSlides <= visibleItems.value) {
    currentIndex.value = nextItemId - (visibleItems.value - amountOfVisibleSlides);
  } else {
    currentIndex.value = nextItemId;
  }

  selectedDotId.value = dotId;
}

function onResize() {
  if (visibleItems.value === setVisibleItemsAmount.value) return;
  visibleItems.value = setVisibleItemsAmount.value;
}

function slideOf(dot, dotsLength) {
  return `${props.paginationLabelStart} ${dot} ${props.paginationLabelMiddle} ${dotsLength}`;
}

function slidesLazyLoading() {
  if (!slidesList) return;

  const slideItems = slidesList.querySelectorAll(SLIDE_SELECTOR);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const listItem = entry.target;
        const [img] = listItem.children;

        if (entry.isIntersecting) {
          img.src = img.dataset.src;
          listItem.style.visibility = VISIBLE;

          if (isMobile.value) {
            listItem.setAttribute('visibility', VISIBLE);
          }
        } else {
          listItem.style.visibility = HIDDEN;

          if (isMobile.value) {
            listItem.setAttribute('visibility', HIDDEN);

            const visibleSlideIndex = Math.max(
              0,
              Array.prototype.indexOf.call(
                slidesList.children,
                slidesList.querySelector(`[visibility=${VISIBLE}]`),
              ),
            );

            setCurrentSlideIndex(
              visibleSlideIndex,
              Math.ceil(visibleSlideIndex / props.mobileCardPerView),
            );
          }
        }
      });
    },
    {
      rootMargin: '-1px',
      threshold: 0.1,
    },
  );
  slideItems.forEach((item) => observer.observe(item));
}

function pushAnalytics(currentIndex) {
  const tag = props.analytics;
  const slide = slidesList.children[currentIndex];

  tag.cta_name = slide.querySelector(SLIDE_LABEL_SELECTOR)?.innerHTML;

  try {
    AnalyticsHandler.getAnalyticsHandler().push(tag);
  } catch (e) {
    console.warn('Could not push to dataLayer', tag);
  }
}
</script>

<style lang="scss">
@import '../../styles/style';
@import 'Slider';
@import '../productCard/style';
</style>
